/**
 * React Router configuration for the Hindi Typing Tutor application.
 *
 * Public static site: every route is open. There are no auth guards and no
 * login/signup/reset pages; old bookmarks to those paths land on the
 * dashboard instead of dead-ending.
 */

import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { Navigate, useParams } from 'react-router-dom';
import React, { lazy } from 'react';
import { getGamePath, getLessonsHubPath, resolveLessonSlug, resolveStorySlug } from '../utils/lessonRoutes';

// Lazy load page components for code splitting
const LandingPage = lazy(() => import('../pages/LandingPage').then(m => ({ default: m.LandingPage })));
const LessonsPage = lazy(() => import('../pages/LessonsPage').then(m => ({ default: m.LessonsPage })));
const TrackExercisePage = lazy(() => import('../pages/TrackExercisePage').then(m => ({ default: m.TrackExercisePage })));
const LessonPracticePage = lazy(() => import('../pages/LessonPracticePage').then(m => ({ default: m.LessonPracticePage })));
const LessonSummaryPage = lazy(() => import('../pages/LessonSummaryPage').then(m => ({ default: m.LessonSummaryPage })));
const StoryPracticePage = lazy(() => import('../pages/StoryPracticePage').then(m => ({ default: m.StoryPracticePage })));
const GameModePage = lazy(() => import('../pages/GameModePage').then(m => ({ default: m.GameModePage })));
const KeyboardLayoutPage = lazy(() => import('../pages/KeyboardLayoutPage').then(m => ({ default: m.KeyboardLayoutPage })));
const NotFoundPage = lazy(() => import('../pages/index').then(m => ({ default: m.NotFoundPage })));

/**
 * Track and story listing bookmarks resolve to their /lessons hub section or
 * filter (GH #124); no separate listing pages remain. A param is required, so
 * these are tiny components rather than static <Navigate> elements.
 */
function TrackListingRedirect() {
  const { trackKey = '' } = useParams<{ trackKey: string }>();
  return <Navigate to={getLessonsHubPath({ track: trackKey })} replace />;
}

function StoryListingRedirect() {
  return <Navigate to={getLessonsHubPath({ section: 'stories' })} replace />;
}

export { TrackListingRedirect, StoryListingRedirect };

/**
 * Canonical short-route wrappers (owner manual-check item 6): resolve the
 * compact slug, then render the existing page with explicit identity. An
 * unknown lesson slug flows into the practice page's honest not-found state;
 * an unknown story slug hits the story loader's existing recovery.
 */
function CanonicalLessonRoute() {
  const { lessonSlug = '' } = useParams<{ lessonSlug: string }>();
  const resolved = resolveLessonSlug(decodeURIComponent(lessonSlug));
  return (
    <LessonPracticePage
      trackKey={resolved?.trackKey}
      exerciseSlug={resolved?.exerciseSlug ?? decodeURIComponent(lessonSlug)}
    />
  );
}

function CanonicalStoryRoute() {
  const { storySlug = '' } = useParams<{ storySlug: string }>();
  const storyId = resolveStorySlug(decodeURIComponent(storySlug));
  return <StoryPracticePage key={storyId ?? 'unknown'} storyId={storyId} />;
}

/**
 * Application router configuration.
 *
 * Route hierarchy:
 * - / (public landing page)
 * - /login, /register, /reset-password (legacy auth paths -> /lessons)
 * - /dashboard (retired -> /lessons)
 * - /lessons (the canonical lessons hub: every track, progress, resume,
 *   preferences/data panel, stories, game entry)
 * - /lessons/:lessonSlug (canonical lesson practice), /lessons/stories/:storySlug
 *   (canonical story practice), /lessons/game (canonical game)
 * - /results/:attemptId (canonical result; identity from the stored attempt)
 * - /tracks/:trackKey (redirects to the hub's track filter)
 * - /tracks/:trackKey/exercises/:exerciseSlug (redirects to practice)
 * - /tracks/:trackKey/exercises/:exerciseSlug/practice
 * - /tracks/:trackKey/exercises/:exerciseSlug/summary
 * - /tracks/:trackKey/exercises/:exerciseSlug/summary/:attemptId
 * - /stories (redirects to the hub's story section)
 * - /stories/:storyId
 * - /game
 * - /onboarding/keyboard-layout
 * - /settings (retired -> hub preferences panel)
 * - * (404)
 */
const router = createBrowserRouter([
  {
    path: '/',
    element: <LandingPage />,
  },
  // Retired auth pages and the retired dashboard (GH #126): old links land on
  // the canonical lessons hub.
  {
    path: '/login',
    element: <Navigate to="/lessons" replace />,
  },
  {
    path: '/register',
    element: <Navigate to="/lessons" replace />,
  },
  {
    path: '/reset-password',
    element: <Navigate to="/lessons" replace />,
  },
  {
    // The hub absorbed the dashboard (GH #126): progress and resume live there.
    path: '/dashboard',
    element: <Navigate to="/lessons" replace />,
  },
  {
    path: '/onboarding/keyboard-layout',
    element: <KeyboardLayoutPage />,
  },
  {
    path: '/lessons',
    element: <LessonsPage />,
  },
  {
    // Canonical game (owner manual-check item 6); static segment outranks
    // the lessonSlug route below.
    path: '/lessons/game',
    element: <GameModePage />,
  },
  {
    // Canonical story practice by slug.
    path: '/lessons/stories/:storySlug',
    element: <CanonicalStoryRoute />,
  },
  {
    // Canonical lesson practice: /lessons/:lessonSlug.
    path: '/lessons/:lessonSlug',
    element: <CanonicalLessonRoute />,
  },
  {
    // Canonical result: identity derives from the stored attempt itself.
    path: '/results/:attemptId',
    element: <LessonSummaryPage />,
  },
  {
    path: '/tracks/:trackKey',
    element: <TrackListingRedirect />,
  },
  {
    path: '/tracks/:trackKey/exercises/:exerciseSlug',
    element: <TrackExercisePage />,
  },
  {
    path: '/tracks/:trackKey/exercises/:exerciseSlug/practice',
    element: <LessonPracticePage />,
  },
  {
    path: '/tracks/:trackKey/exercises/:exerciseSlug/summary',
    element: <LessonSummaryPage />,
  },
  {
    // Durable deep-link form: the attempt id in the URL lets the summary page
    // re-read the result from device-local storage when navigation state is
    // gone (refresh, bookmark, new tab on the same device).
    path: '/tracks/:trackKey/exercises/:exerciseSlug/summary/:attemptId',
    element: <LessonSummaryPage />,
  },
  {
    // Retired standalone listing (GH #124): the hub's story shelf answers it.
    path: '/stories',
    element: <StoryListingRedirect />,
  },
  {
    path: '/stories/:storyId',
    element: <StoryPracticePage />,
  },
  {
    // Legacy game bookmark: the canonical home is /lessons/game.
    path: '/game',
    element: <Navigate to={getGamePath()} replace />,
  },
  {
    // The hub's preferences/data panel answers old settings links (GH #126).
    path: '/settings',
    element: <Navigate to={getLessonsHubPath({ panel: 'preferences' })} replace />,
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);

/**
 * AppRouter component that provides the router to the application.
 */
export function AppRouter() {
  return (
    <React.Suspense fallback={<div className="p-6 text-gray-600">Loading...</div>}>
      <RouterProvider router={router} />
    </React.Suspense>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export default router;
